/**
 * 本地传手机模式的会话控制器：把 `LocalEngineTransport` 的消息流接到三个 store 上。
 *
 * 这是**唯一**知道「当前是本地模式」的地方。牌桌、行动条、动画层只读 store，
 * 因此换成 WsTransport 时这个文件是唯一要改的（§11.7：UI 里不允许出现 mode 分支）。
 *
 * 遮牌时序是本文件的核心（§11.8）：
 *   · `perspectiveChanged(reason:'turn')` → 立刻遮（不透明、零动画）
 *   · 长按揭示 → `unveil()`
 *   · 提交行动 → **在 send 之前**就遮上，先遮再播动画
 *   · `perspectiveChanged(reason:'handEnd')` → 揭开，全桌一起看摊牌（此时视角是旁观，
 *     快照里只剩公开亮出的牌，盖牌的人不会被当众翻出来）
 */
import { create } from 'zustand'
import type { AppliedAction, Chips, SeatIndex } from '@poker/shared'

import { useTableStore, useUiStore } from '../store/index.js'
import {
  createLocalEngineTransport,
  type LocalEngineTransport,
  type LocalSetup,
} from '../transport/LocalEngineTransport.js'
import type { ActionKind, ServerMessage } from '../transport/types.js'
import { formatChips } from './format.js'
import { localSeatNote, nameOfSeat, type LocalPlayer } from './localSeats.js'

export type LocalSessionStatus = 'idle' | 'running' | 'ended'

export interface LocalSessionState {
  readonly status: LocalSessionStatus
  readonly players: readonly LocalPlayer[]
  /** 当前该看牌的人；null = 旁观（摊牌时） */
  readonly perspectiveSeat: SeatIndex | null
  readonly perspectiveName: string | null
  /** 遮牌页上显示的「上一个人做了什么」，递错手机时对方也能察觉 */
  readonly lastActionText: string | null
  readonly handNo: number
  readonly error: string | null
}

const EMPTY: LocalSessionState = {
  status: 'idle',
  players: [],
  perspectiveSeat: null,
  perspectiveName: null,
  lastActionText: null,
  handNo: 0,
  error: null,
}

export const useLocalSessionStore = create<LocalSessionState>(() => ({ ...EMPTY }))

// ============================================================================
// 动作播报
// ============================================================================

/** 「张三 加注到 $200」。遮牌页与牌桌气泡共用这一份措辞，避免两处描述同一件事却不一致。 */
export function formatActionText(name: string, a: AppliedAction): string {
  const amount = formatChips(a.to)
  switch (a.type) {
    case 'fold':
      return `${name} 弃牌`
    case 'check':
      return `${name} 过牌`
    case 'call':
      return a.allIn ? `${name} 跟注全下 $${amount}` : `${name} 跟注 $${amount}`
    case 'bet':
      return a.allIn ? `${name} 全下 $${amount}` : `${name} 下注 $${amount}`
    case 'raise':
      return a.allIn ? `${name} 全下 $${amount}` : `${name} 加注到 $${amount}`
    case 'postSmallBlind':
      return `${name} 小盲 $${amount}`
    case 'postBigBlind':
      return `${name} 大盲 $${amount}`
    case 'postAnte':
      return `${name} 底注 $${amount}`
    case 'postDead':
      // 补的小盲不算本轮投入，`to` 不动，金额只能取本次实际掏出的 delta
      return `${name} 补小盲 $${formatChips(a.delta)}`
  }
}

// ============================================================================
// 会话
// ============================================================================

let transport: LocalEngineTransport | null = null
let unsubscribe: (() => void) | null = null
let actionSeq = 0

function handle(m: ServerMessage): void {
  const table = useTableStore.getState()
  const ui = useUiStore.getState()

  switch (m.type) {
    case 'welcome':
      table.setConnection('live')
      break

    case 'roomSnapshot': {
      table.setRoom(null, m.config)
      table.setSeats(
        m.seats.map((s) => ({
          seat: s.seat,
          name: s.name,
          stack: s.stack,
          isBot: s.isBot,
          note: localSeatNote(s),
          // 本地传手机没有账号概念，没有自定义头像可言
          avatarSeed: '',
          accountId: '',
        })),
      )
      useLocalSessionStore.setState({
        status: m.phase === 'ended' ? 'ended' : m.phase === 'running' ? 'running' : 'idle',
        handNo: m.handNo,
      })
      break
    }

    case 'handSnapshot':
      // heroSeat 只在视角落到具体座位时更新：摊牌时视角是 null，但牌桌不能因此转回
      // 「0 号位在下方」—— 那会在最该看清结果的时刻把整桌人转一圈。
      // 底牌的可见性由 heroHoleCards() 按 view.viewerSeat 判定，与布局朝向无关。
      if (m.view.viewerSeat !== null) table.setHeroSeat(m.view.viewerSeat)
      table.applyHandView(m.view, m.legal)
      break

    case 'perspectiveChanged': {
      useLocalSessionStore.setState({ perspectiveSeat: m.seat, perspectiveName: m.name })
      if (m.reason === 'turn') {
        // 零动画、纯色、条件渲染 —— 三件事都由 uiStore.veil + .veil 样式 + App 的条件挂载保证
        ui.veil('pass', m.seat)
      } else if (m.reason === 'handEnd') {
        ui.unveil()
      }
      break
    }

    case 'handStarted':
      useLocalSessionStore.setState({ lastActionText: null, handNo: m.handNo })
      break

    case 'playerActed': {
      const name = nameOfSeat(useLocalSessionStore.getState().players, m.action.seat)
      useLocalSessionStore.setState({ lastActionText: formatActionText(name, m.action) })
      break
    }

    case 'error':
      useLocalSessionStore.setState({ error: m.message })
      ui.showToast(m.message, 'warn')
      break

    default:
      break
  }
}

export interface LocalSessionController {
  readonly transport: LocalEngineTransport
  start(): void
  submitAction(action: ActionKind, to?: Chips): void
  reveal(): void
  nextHand(): void
  leave(): void
}

/**
 * 开一局本地牌局。会替换掉已有的会话（先 close 再建，不留悬挂定时器）。
 */
export function startLocalGame(setup: LocalSetup): LocalSessionController {
  leaveLocalGame()

  const t = createLocalEngineTransport(setup)
  transport = t
  useLocalSessionStore.setState({ ...EMPTY, players: setup.players.slice() })
  // 刷新/重开后必须先遮（红线 #12 第 12 条）
  useUiStore.getState().veil('init', null)
  useUiStore.getState().setView('table')
  unsubscribe = t.onMessage(handle)
  void t.connect()
  t.send({ type: 'startGame' })
  return controller(t)
}

function controller(t: LocalEngineTransport): LocalSessionController {
  return {
    transport: t,
    start: () => t.send({ type: 'startGame' }),
    submitAction: (action, to) => submitLocalAction(action, to),
    reveal: revealLocalHand,
    nextHand: () => t.send({ type: 'nextHand' }),
    leave: leaveLocalGame,
  }
}

/**
 * 提交行动。**先遮牌再发送**（§11.8 e）：
 * 如果等引擎回包再遮，中间那几十毫秒里下一位玩家已经能看到上一位的底牌了。
 */
export function submitLocalAction(action: ActionKind, to?: Chips): void {
  const t = transport
  const view = useTableStore.getState().view
  if (t === null || view === null || view.result !== null) return

  useUiStore.getState().veil('pass', null)
  useTableStore.getState().setPending(
    action === 'bet' || action === 'raise'
      ? { type: action, to: to ?? 0 }
      : action === 'allIn'
        ? { type: 'allIn' }
        : { type: action },
  )

  actionSeq += 1
  t.send({
    type: 'playerAction',
    handId: view.handId,
    action,
    to,
    actionNo: view.actionLog.length,
    clientActionId: `local-${actionSeq}`,
  })
}

/** 长按 500ms 完成后调用。这是遮牌页唯一的出口。 */
export function revealLocalHand(): void {
  useUiStore.getState().unveil()
}

export function nextLocalHand(): void {
  transport?.send({ type: 'nextHand' })
}

export function leaveLocalGame(): void {
  unsubscribe?.()
  unsubscribe = null
  transport?.close()
  transport = null
  useLocalSessionStore.setState({ ...EMPTY })
  useTableStore.getState().reset()
  useUiStore.getState().reset()
}

/** 当前会话（可能为 null）。组件里一般不需要它，行动条用 `submitLocalAction` 即可。 */
export function currentLocalTransport(): LocalEngineTransport | null {
  return transport
}
